import type { Building } from "building-types/building/buildingTypes";
import type { BuildingEntityFilter } from "building-types/dataSequences/filterableBuildingEntityTypes";
import type {
  EntityKeyAttributes,
  KeyAttributesAndMeasures,
  EntityKeyMeasures,
  BuildingAreaKeyAttributes,
  BuildingOptionKeyAttributes,
  BuildingPresetKeyAttributes,
  KeyAttributesAndMeasuresOptions,
  AggregatedKeyAttributesAndMeasures,
  KeyAttributesAndMeasuresSortOrder,
} from "building-types/dataSequences/measures";
import type { SystemDataSequenceEntityType } from "building-types/dataSequences/systemDataSequenceTypes";
import * as bdg from "./builtin/building";
import * as casework from "./builtin/casework";
import * as ceiling from "./builtin/ceilings";
import * as door from "./builtin/doors";
import * as electrical from "./builtin/electrical";
import * as exteriorTrim from "./builtin/exteriorTrim";
import * as flooring from "./builtin/flooring";
import { makeFloorMaterialSummaryDataTable } from "./builtin/floorMaterialSummary";
import * as foundation2 from "./builtin/foundations";
import * as framing from "./builtin/framing";
import * as interiorFinish from "./builtin/interiorFinishes";
import * as millwork from "./builtin/millwork";
import * as prop from "./builtin/props";
import * as roof from "./builtin/roofs";
import * as wall from "./builtin/walls";
import * as window from "./builtin/windows";
import type { BuiltInDataTable } from "./dataTableTypes";

const DEFINES_BUILDING_AREA: (keyof BuildingAreaKeyAttributes)[] = ["category", "name"];
const DEFINES_BUILDING_OPTION: (keyof BuildingOptionKeyAttributes)[] = ["optionKey"];
const DEFINES_BUILDING_PRESET: (keyof BuildingPresetKeyAttributes)[] = ["presetId", "builderOptionCode"];

export const BUILTIN_DATASEQUENCE_CATALOG = {
  BUILDING_AREA: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "BUILDING_AREA",
    selector: bdg.buildingAreaKAMs,
    emptyKAM: bdg.EMPTY_BUILDING_AREA_KAM,
    definesEntityAttributes: DEFINES_BUILDING_AREA,
    options: bdg.makeDefaultBuildingAreaKAMOptions(),
    sortOrder: bdg.defaultBuildingAreaKAMSort,
  },
  BUILDING_OPTION: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "BUILDING_OPTION",
    selector: (building: Building) => bdg.buildingOptionKAMs(building).options,
    emptyKAM: bdg.EMPTY_BUILDING_OPTION_KAM,
    definesEntityAttributes: DEFINES_BUILDING_OPTION,
    options: bdg.makeDefaultBuildingOptionKAMOptions(),
    sortOrder: bdg.defaultBuildingOptionKAMSort,
  },
  BUILDING_PRESET: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "BUILDING_PRESET",
    selector: (building: Building) => bdg.buildingOptionKAMs(building).presets,
    emptyKAM: bdg.EMPTY_BUILDING_PRESET_KAM,
    definesEntityAttributes: DEFINES_BUILDING_PRESET,
    options: bdg.makeDefaultBuildingPresetKAMOptions(),
    sortOrder: bdg.defaultBuildingPresetKAMSort,
  },
  CASEWORK: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "CASEWORK",
    selector: casework.buildingCaseworkKAMs,
    emptyKAM: casework.EMPTY_CASEWORK_KAM,
    definesEntityAttributes: casework.DEFINES_CASEWORK,
    options: casework.makeDefaultCaseworkKAMOptions(),
    sortOrder: casework.defaultCaseworkKAMSort,
  },
  CEILING: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "CEILING",
    selector: ceiling.buildingCeilingKAMs,
    emptyKAM: ceiling.EMPTY_CEILING_KAM,
    definesEntityAttributes: ceiling.DEFINES_CEILING,
    options: ceiling.makeDefaultCeilingKAMOptions(),
    sortOrder: ceiling.defaultCeilingKAMSort,
  },
  DOOR: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "DOOR",
    selector: door.buildingDoorKAMs,
    emptyKAM: door.EMPTY_DOOR_KAM,
    definesEntityAttributes: door.DEFINES_DOOR,
    options: door.makeDefaultDoorKAMOptions(),
    sortOrder: door.defaultDoorKAMSort,
  },
  ELECTRICAL: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "ELECTRICAL",
    selector: electrical.buildingElectricalKAMs,
    emptyKAM: electrical.EMPTY_ELECTRICAL_KAM,
    definesEntityAttributes: electrical.DEFINES_ELECTRICAL,
    options: electrical.makeDefaultElectricalKAMOptions(),
    sortOrder: electrical.defaultElectricalKAMSort,
  },
  /**
   * @TODO - this has not been implemented as a built-in data sequence
   */
  EXTERIOR_FINISH: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "EXTERIOR_FINISH",
    selector: (_: Building) => [],
    emptyKAM: {
      type: "EXTERIOR_FINISH",
      attributes: {},
      measures: {},
      handles: [],
    },
    definesEntityAttributes: [] as string[],
    options: {
      attributes: {},
      measures: {},
    },
    sortOrder: () => 1,
  },
  EXTERIOR_TRIM: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "EXTERIOR_TRIM",
    selector: exteriorTrim.buildingExteriorTrimKAMs,
    emptyKAM: exteriorTrim.EMPTY_EXTERIOR_TRIM_KAM,
    definesEntityAttributes: exteriorTrim.DEFINES_EXTERIOR_TRIM,
    options: exteriorTrim.makeDefaultExteriorTrimKAMOptions(),
    sortOrder: exteriorTrim.defaultExteriorTrimKAMSort,
  },
  FLOORING: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "FLOORING",
    selector: (building: Building, filter?: BuildingEntityFilter) => flooring.buildingFlooringKAMs(building, filter),
    emptyKAM: flooring.EMPTY_FLOORING_KAM,
    definesEntityAttributes: flooring.DEFINES_FLOORING,
    options: flooring.makeDefaultFlooringKAMOptions(),
    sortOrder: flooring.defaultFlooringKAMSort,
  },
  FOOTING: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "FOOTING",
    selector: (building: Building, filter?: BuildingEntityFilter) =>
      foundation2.buildingFoundationKAMs(building, filter).footings,
    emptyKAM: foundation2.EMPTY_FOOTING_FOUNDATION_KAM,
    definesEntityAttributes: foundation2.DEFINES_FOOTING,
    options: foundation2.makeDefaultFootingFoundationKAMOptions(),
    sortOrder: foundation2.defaultFootingFoundationKAMSort,
  },
  FRAMING: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "FRAMING",
    selector: (building: Building, filter?: BuildingEntityFilter) => framing.buildingFramingKAMs(building, filter),
    emptyKAM: framing.EMPTY_FRAMING_KAM,
    definesEntityAttributes: framing.DEFINES_FRAMING,
    options: framing.makeDefaultFramingKAMOptions(),
    sortOrder: framing.defaultFramingKAMSort,
  },
  INTERIOR_FINISH: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "INTERIOR_FINISH",
    selector: (building: Building, filter?: BuildingEntityFilter) =>
      interiorFinish.buildingWallInteriorFinishKAMs(building, filter),
    emptyKAM: interiorFinish.EMPTY_WALL_INTERIOR_FINISH_KAM,
    definesEntityAttributes: interiorFinish.DEFINES_WALL_INTERIOR_FINISH,
    options: interiorFinish.makeDefaultWallInteriorFinishKAMOptions(),
    sortOrder: interiorFinish.defaultWallInteriorFinishKAMSort,
  },
  MILLWORK: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "MILLWORK",
    selector: (building: Building, filter?: BuildingEntityFilter) => millwork.buildingMillworkKAMs(building, filter),
    emptyKAM: millwork.EMPTY_MILLWORK_KAM,
    definesEntityAttributes: millwork.DEFINES_MILLWORK,
    options: millwork.makeDefaultMillworkKAMOptions(),
    sortOrder: millwork.defaultMillworkKAMSort,
  },
  OPENING_CASING: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "OPENING_CASING",
    selector: exteriorTrim.buildingOpeningCasingKAMs,
    emptyKAM: exteriorTrim.EMPTY_OPENING_CASING_KAM,
    definesEntityAttributes: exteriorTrim.DEFINES_OPENING_CASING,
    options: exteriorTrim.makeDefaultOpeningCasingKAMOptions(),
    sortOrder: exteriorTrim.defaultOpeningCasingKAMSort,
  },
  OPENING_TRIM: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "OPENING_TRIM",
    selector: (building: Building, filter?: BuildingEntityFilter) => millwork.buildingOpeningTrimKAMs(building, filter),
    emptyKAM: millwork.EMPTY_OPENING_TRIM_KAM,
    definesEntityAttributes: millwork.DEFINES_OPENING_TRIM,
    options: millwork.makeDefaultOpeningTrimKAMOptions(),
    sortOrder: millwork.defaultOpeningTrimKAMSort,
  },
  PROP: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "PROP",
    selector: prop.buildingPropKAMs,
    emptyKAM: prop.EMPTY_PROP_KAM,
    definesEntityAttributes: prop.DEFINES_PROP,
    options: prop.makeDefaultPropKAMOptions(),
    sortOrder: prop.defaultPropKAMSort,
  },
  ROOF: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "ROOF",
    selector: (building: Building, filter?: BuildingEntityFilter) => roof.buildingRoofKAMs(building, filter).roofs,
    emptyKAM: roof.EMPTY_ROOF_KAM,
    definesEntityAttributes: roof.DEFINES_ROOF,
    options: roof.makeDefaultRoofKAMOptions(),
    sortOrder: roof.defaultRoofKAMSort,
  },
  ROOF_ANALYTIC_AREA: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "ROOF_ANALYTIC_AREA",
    selector: (building: Building, filter?: BuildingEntityFilter) =>
      roof.buildingRoofKAMs(building, filter).analyticAreas,
    emptyKAM: roof.EMPTY_ROOF_ANALYTIC_AREA_KAM,
    definesEntityAttributes: roof.DEFINES_ROOF_ANALYTIC_AREA,
    options: roof.makeDefaultRoofAnalyticAreaKAMOptions(),
    sortOrder: roof.defaultRoofAnalyticAreaKAMSort,
  },
  ROOF_ANALYTIC_LENGTH: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "ROOF_ANALYTIC_LENGTH",
    selector: (building: Building, filter?: BuildingEntityFilter) =>
      roof.buildingRoofKAMs(building, filter).analyticLengths,
    emptyKAM: roof.EMPTY_ROOF_ANALYTIC_LENGTH_KAM,
    definesEntityAttributes: roof.DEFINES_ROOF_ANALYTIC_LENGTH,
    options: roof.makeDefaultRoofAnalyticLengthKAMOptions(),
    sortOrder: roof.defaultRoofAnalyticLengthKAMSort,
  },
  ROOF_AREA: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "ROOF_AREA",
    selector: (building: Building, filter?: BuildingEntityFilter) => roof.buildingRoofKAMs(building, filter).areas,
    emptyKAM: roof.EMPTY_ROOF_AREA_KAM,
    definesEntityAttributes: roof.DEFINES_ROOF_AREA,
    options: roof.makeDefaultRoofAreaKAMOptions(),
    sortOrder: roof.defaultRoofAreaKAMSort,
  },
  ROOF_EAVE: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "ROOF_EAVE",
    selector: (building: Building, filter?: BuildingEntityFilter) => roof.buildingRoofKAMs(building, filter).eaves,
    emptyKAM: roof.EMPTY_ROOF_EAVE_KAM,
    definesEntityAttributes: roof.DEFINES_LINEAR_ROOF,
    options: roof.makeDefaultRoofEaveKAMOptions(),
    sortOrder: roof.defaultRoofEaveKAMSort,
  },
  ROOF_FLASHING: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "ROOF_FLASHING",
    selector: (building: Building, filter?: BuildingEntityFilter) =>
      roof.buildingRoofKAMs(building, filter).wallFlashing,
    emptyKAM: roof.EMPTY_ROOF_FLASHING_KAM,
    definesEntityAttributes: roof.DEFINES_LINEAR_ROOF,
    options: roof.makeDefaultRoofFlashingKAMOptions(),
    sortOrder: roof.defaultRoofFlashingKAMSort,
  },
  ROOF_RIDGE: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "ROOF_RIDGE",
    selector: (building: Building, filter?: BuildingEntityFilter) => roof.buildingRoofKAMs(building, filter).ridges,
    emptyKAM: roof.EMPTY_ROOF_RIDGE_KAM,
    definesEntityAttributes: roof.DEFINES_LINEAR_ROOF,
    options: roof.makeDefaultRoofRidgeKAMOptions(),
    sortOrder: roof.defaultRoofRidgeKAMSort,
  },
  ROOF_RIDGE_VENT: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "ROOF_RIDGE_VENT",
    selector: (building: Building, filter?: BuildingEntityFilter) => roof.buildingRoofKAMs(building, filter).ridgeVents,
    emptyKAM: roof.EMPTY_ROOF_RIDGE_VENT_KAM,
    definesEntityAttributes: roof.DEFINES_LINEAR_ROOF,
    options: roof.makeDefaultRoofRidgeVentKAMOptions(),
    sortOrder: roof.defaultRoofRidgeVentKAMSort,
  },
  ROOF_VALLEY: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "ROOF_VALLEY",
    selector: (building: Building, filter?: BuildingEntityFilter) => roof.buildingRoofKAMs(building, filter).valleys,
    emptyKAM: roof.EMPTY_ROOF_VALLEY_KAM,
    definesEntityAttributes: roof.DEFINES_LINEAR_ROOF,
    options: roof.makeDefaultRoofValleyKAMOptions(),
    sortOrder: roof.defaultRoofValleyKAMSort,
  },
  SLAB: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "SLAB",
    selector: (building: Building, filter?: BuildingEntityFilter) =>
      foundation2.buildingFoundationSuperSystemKAMs(building, filter).slabs,
    emptyKAM: foundation2.EMPTY_SLAB_FOUNDATION_KAM,
    definesEntityAttributes: foundation2.DEFINES_SLAB,
    options: foundation2.makeDefaultSlabFoundationKAMOptions(),
    sortOrder: foundation2.defaultSlabFoundationKAMSort,
  },
  WALL: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "WALL",
    selector: (building: Building, filter?: BuildingEntityFilter) => wall.buildingWallKAMs(building, filter),
    emptyKAM: wall.EMPTY_WALL_KAM,
    definesEntityAttributes: wall.DEFINES_WALL,
    options: wall.makeDefaultWallKAMOptions(),
    sortOrder: wall.defaultWallKAMSort,
  },
  WALL_AREA: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "WALL_AREA",
    selector: wall.buildingWallAreaKAMs,
    emptyKAM: wall.EMPTY_WALL_AREA_KAM,
    definesEntityAttributes: wall.DEFINES_WALL_AREA,
    options: wall.makeDefaultWallAreaKAMOptions(),
    sortOrder: wall.defaultWallAreaKAMSort,
  },
  WINDOW: {
    dataSequenceType: "KAM_SEQUENCE",
    type: "WINDOW",
    selector: window.buildingWindowKAMs,
    emptyKAM: window.EMPTY_WINDOW_KAM,
    definesEntityAttributes: window.DEFINES_WINDOW,
    options: window.makeDefaultWindowKAMOptions(),
    sortOrder: window.defaultWindowKAMSort,
  },
  FLOOR_MATERIALS_SUMMARY: {
    dataSequenceType: "TABLE_SEQUENCE",
    type: "FLOOR_MATERIALS_SUMMARY",
    generateTable: makeFloorMaterialSummaryDataTable,
  },
} satisfies {
  [K in SystemDataSequenceEntityType]: BuiltInDataSequenceSelector<K>;
};

export type BuiltInDataSequenceCatalog = typeof BUILTIN_DATASEQUENCE_CATALOG;
export type BuildingEntityKAMSelector = (
  building: Building,
  filter?: BuildingEntityFilter,
) => KeyAttributesAndMeasures<any, any>[];

export interface BuiltInKAMDataSequenceSelector<T extends SystemDataSequenceEntityType> {
  readonly dataSequenceType: "KAM_SEQUENCE";
  readonly type: T;
  readonly selector: BuildingEntityKAMSelector;
  readonly emptyKAM: KeyAttributesAndMeasures<EntityKeyAttributes, EntityKeyMeasures>;
  readonly definesEntityAttributes: (keyof EntityKeyAttributes)[];
  readonly options: KeyAttributesAndMeasuresOptions<EntityKeyAttributes, EntityKeyMeasures>;
  readonly sortOrder: KeyAttributesAndMeasuresSortOrder<any, any>;
}

export type BuiltInKAMDataSequence<T extends SystemDataSequenceEntityType> = {
  readonly type: T;
  readonly kams: KeyAttributesAndMeasures<EntityKeyAttributes, EntityKeyMeasures>[];
  readonly aggregatedKAMs: AggregatedKeyAttributesAndMeasures<EntityKeyAttributes, EntityKeyMeasures>[];
};

// Re-export from shared types for backwards compatibility
export type { BuiltInDataTableCell, BuiltInDataTable } from "./dataTableTypes";

export interface BuiltInTableDataSequenceSelector<T extends SystemDataSequenceEntityType> {
  readonly dataSequenceType: "TABLE_SEQUENCE";
  readonly type: T;
  /**
   *
   * @param building - if undefined will return an empty table
   * @returns
   */
  readonly generateTable: (building?: Building | undefined) => BuiltInDataTable;
}

export type BuiltInDataSequenceSelector<T extends SystemDataSequenceEntityType> =
  | BuiltInKAMDataSequenceSelector<T>
  | BuiltInTableDataSequenceSelector<T>;
